import { useMemo, useRef, useState, type ChangeEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SplitBar } from '@/components/charts/SplitBar';
import { AmountText } from '@/components/AmountText';
import type { AuditBatchUndoData, BatchUndoInput } from '@/api/hooks/useBatchUndo';
import type { SplitTransactionData, SplitTransactionRequest } from '@/api/hooks/useSplitTransaction';
import type { MutationResult } from '@/api/hooks/useLineItemPatch';
import { decimalIsZero, decimalSubtract, decimalSum } from './decimal';

const SCALE = 10_000n;
const SCALE_DIVISOR = 1_000_000n;
const SCALE_FOUR_RE = /^-?(?:0|[1-9][0-9]*)(?:\.[0-9]{1,4})?$/;
const MONTH_RE = /^([0-9]{4})-([0-9]{2})$/;

export type SplitMode = SplitTransactionRequest['mode'];

export type SplitCategoryOption = {
  category_id: string;
  name: string;
  taxonomy_version: string;
};

export type SplitLineItem = {
  line_item_id: string;
  transaction_id?: string;
  amount: string;
  category_id?: string | null;
  category_slug?: string | null;
  category_name?: string | null;
  version?: number;
  business_state?: 'business' | 'personal';
  is_business?: boolean;
  allocation_month?: string | null;
  allocation_percent?: string | null;
};

type ExpectedLineItem = { line_item_id: string; version: number };

type SplitMutationInput = {
  transactionId: string;
  input: Omit<SplitTransactionRequest, 'operation_id'> & { operation_id?: string };
};

type SplitMutationAdapter = {
  mutateAsync: (input: SplitMutationInput) => Promise<MutationResult<SplitTransactionData>>;
  isPending?: boolean;
};

type UndoMutationAdapter = {
  mutateAsync: (input: BatchUndoInput) => Promise<MutationResult<AuditBatchUndoData>>;
  isPending?: boolean;
};

const unwiredMutation: SplitMutationAdapter = {
  isPending: false,
  mutateAsync: async () => ({ kind: 'unavailable', reason: 'not_wired', message: 'The split service is not available.' }),
};

const unwiredUndoMutation: UndoMutationAdapter = {
  isPending: false,
  mutateAsync: async () => ({ kind: 'unavailable', reason: 'not_wired', message: 'The split Undo service is not available.' }),
};

export type SplitEditorProps = {
  totalAmount: string;
  currency: string;
  lineItems: SplitLineItem[];
  transactionId?: string;
  revision?: number;
  expectedLineItems?: ExpectedLineItem[];
  categories?: SplitCategoryOption[];
  initialMonth?: string;
  onCancel?: () => void;
  onRefresh?: () => void;
  onSaved?: (data: SplitTransactionData) => void;
  onUndone?: (data: AuditBatchUndoData) => void;
  splitMutation?: SplitMutationAdapter;
  undoMutation?: UndoMutationAdapter;
};

type DraftPart = {
  clientPartId: string;
  amount: string;
  percent: string;
  categoryId: string;
  taxonomyVersion: string;
  businessState: 'business' | 'personal';
  month: string;
};

type ActionState =
  | { kind: 'idle' }
  | { kind: 'saving' }
  | { kind: 'saved'; batchId: string; mode: SplitMode; count: number; replay: boolean }
  | { kind: 'save-error'; message: string; refresh: boolean }
  | { kind: 'undoing' }
  | { kind: 'undo-error'; message: string; refresh: boolean };

function newUuid(): string {
  const value = globalThis.crypto?.randomUUID?.();
  if (!value) throw new Error('Split editing needs UUID support to identify each part.');
  return value;
}

function parseScaled(value: string): bigint | null {
  if (!SCALE_FOUR_RE.test(value)) return null;
  const negative = value.startsWith('-');
  const unsigned = negative ? value.slice(1) : value;
  const [whole, fraction = ''] = unsigned.split('.');
  const scaled = BigInt(whole) * SCALE + BigInt(fraction.padEnd(4, '0'));
  return negative ? -scaled : scaled;
}

function canonicalScaleFour(value: string): string {
  const parsed = parseScaled(value);
  if (parsed === null) return '';
  return formatScaled(parsed);
}

function formatScaled(value: bigint): string {
  const negative = value < 0n;
  const magnitude = negative ? -value : value;
  const digits = magnitude.toString().padStart(5, '0');
  return `${negative ? '-' : ''}${digits.slice(0, -4)}.${digits.slice(-4)}`;
}

function signedMagnitude(value: bigint, negative: boolean): string {
  return formatScaled(negative ? -value : value);
}

function equalAmounts(totalAmount: string, count: number): string[] {
  const parsed = parseScaled(canonicalScaleFour(totalAmount));
  if (parsed === null || count < 1) return Array.from({ length: count }, () => '');
  const negative = parsed < 0n;
  const magnitude = negative ? -parsed : parsed;
  const base = magnitude / BigInt(count);
  const remainder = magnitude % BigInt(count);
  return Array.from({ length: count }, (_, index) => signedMagnitude(base + (BigInt(index) < remainder ? 1n : 0n), negative));
}

function equalPercents(count: number): string[] {
  const totalUnits = 100n * SCALE;
  const base = totalUnits / BigInt(count);
  const remainder = totalUnits % BigInt(count);
  return Array.from({ length: count }, (_, index) => formatScaled(base + (BigInt(index) < remainder ? 1n : 0n)));
}

function percentForAmount(amount: string, totalAmount: string): string {
  const parsedAmount = parseScaled(amount);
  const parsedTotal = parseScaled(totalAmount);
  if (parsedAmount === null || parsedTotal === null || parsedTotal === 0n) return '';
  const amountMagnitude = parsedAmount < 0n ? -parsedAmount : parsedAmount;
  const totalMagnitude = parsedTotal < 0n ? -parsedTotal : parsedTotal;
  return formatScaled((amountMagnitude * 100n * SCALE + totalMagnitude / 2n) / totalMagnitude);
}

function monthIndex(value: string): number | null {
  const match = MONTH_RE.exec(value);
  if (!match) return null;
  const month = Number(match[2]);
  if (month < 1 || month > 12) return null;
  return Number(match[1]) * 12 + month - 1;
}

function monthFromIndex(value: number): string {
  const year = Math.floor(value / 12);
  const month = (value % 12) + 1;
  return `${year.toString().padStart(4, '0')}-${month.toString().padStart(2, '0')}`;
}

function consecutiveMonths(start: string, count: number): string[] {
  const index = monthIndex(start);
  return index === null ? Array.from({ length: count }, () => '') : Array.from({ length: count }, (_, offset) => monthFromIndex(index + offset));
}

function lineItemPart(item: SplitLineItem, categories: SplitCategoryOption[]): DraftPart {
  const category = categories.find((candidate) => candidate.category_id === item.category_id);
  return {
    clientPartId: newUuid(),
    amount: canonicalScaleFour(item.amount),
    percent: item.allocation_percent ? canonicalScaleFour(item.allocation_percent) : '',
    categoryId: item.category_id ?? '',
    taxonomyVersion: category?.taxonomy_version ?? '',
    businessState: item.business_state ?? (item.is_business ? 'business' : 'personal'),
    month: item.allocation_month ?? '',
  };
}

function partsFromLineItems(lineItems: SplitLineItem[], categories: SplitCategoryOption[]): DraftPart[] {
  return lineItems.map((item) => lineItemPart(item, categories));
}

function messageForError(error: unknown, action: 'save' | 'undo'): { message: string; refresh: boolean } {
  const candidate = error as { code?: string; status?: number } | null;
  if (candidate?.code === 'changed_since' || candidate?.status === 409) {
    return action === 'save'
      ? { message: 'This split changed since you loaded it. Refresh before saving.', refresh: true }
      : { message: 'This split changed since you loaded it. Refresh before trying Undo.', refresh: true };
  }
  const unknownCode = candidate?.code === undefined || candidate.code === 'unknown' || candidate.code === 'request_failed';
  if ((candidate?.status === undefined && unknownCode) || (candidate?.status !== undefined && candidate.status >= 500)) {
    return action === 'save'
      ? { message: "We couldn't confirm the split. Refresh to check the current split.", refresh: true }
      : { message: "We couldn't confirm the split Undo. Refresh to check the current split.", refresh: true };
  }
  return action === 'save'
    ? { message: "Couldn't save this split. Try again.", refresh: false }
    : { message: "Couldn't undo this split. Try again.", refresh: false };
}

function isSuccess<T>(result: MutationResult<T>): result is Extract<MutationResult<T>, { kind: 'success' | 'replay' }> {
  return result.kind === 'success' || result.kind === 'replay';
}

export function SplitEditor({
  totalAmount,
  currency,
  lineItems,
  transactionId,
  revision = 1,
  expectedLineItems,
  categories = [],
  initialMonth,
  onCancel,
  onRefresh,
  onSaved,
  onUndone,
  splitMutation,
  undoMutation,
}: SplitEditorProps) {
  const queryClient = useQueryClient();
  const openButtonRef = useRef<HTMLButtonElement>(null);
  const [mode, setMode] = useState<SplitMode>('amount');
  const [sourceItems, setSourceItems] = useState(lineItems);
  const [expectedItems, setExpectedItems] = useState<ExpectedLineItem[]>(() => expectedLineItems ?? lineItems.map((item) => ({ line_item_id: item.line_item_id, version: item.version ?? 1 })));
  const [draft, setDraft] = useState(() => partsFromLineItems(lineItems, categories));
  const [editing, setEditing] = useState(true);
  const [action, setAction] = useState<ActionState>({ kind: 'idle' });

  const splitWriter = splitMutation ?? unwiredMutation;
  const undoWriter = undoMutation ?? unwiredUndoMutation;
  const categoryById = useMemo(() => new Map(categories.map((category) => [category.category_id, category])), [categories]);
  const expected = expectedItems;

  const resolvedDraft = useMemo(() => {
    if (mode === 'percent') {
      const total = parseScaled(canonicalScaleFour(totalAmount));
      const percentages = draft.map((part) => parseScaled(part.percent));
      if (total === null || percentages.some((value) => value === null)) return draft;
      const negative = total < 0n;
      const totalMagnitude = negative ? -total : total;
      const percentageValues = percentages as bigint[];
      const percentageTotal = percentageValues.reduce((sum, value) => sum + value, 0n);
      const amounts = percentageValues.map((percentage) => ((totalMagnitude * percentage) + 500_000n) / SCALE_DIVISOR);
      if (percentageTotal === 100n * SCALE && amounts.length > 0) {
        const priorTotal = amounts.slice(0, -1).reduce((sum, amount) => sum + amount, 0n);
        amounts[amounts.length - 1] = totalMagnitude - priorTotal;
      }
      return draft.map((part, index) => {
        const percentage = percentageValues[index] ?? 0n;
        const amount = amounts[index] ?? ((totalMagnitude * percentage + 500_000n) / SCALE_DIVISOR);
        return { ...part, amount: signedMagnitude(amount, negative) };
      });
    }
    if (mode === 'months') {
      const amounts = equalAmounts(totalAmount, draft.length);
      return draft.map((part, index) => ({ ...part, amount: amounts[index] ?? '' }));
    }
    return draft;
  }, [draft, mode, totalAmount]);

  const validity = useMemo(() => {
    const parsedAmounts = resolvedDraft.map((part) => parseScaled(part.amount));
    const hasValidAmounts = parsedAmounts.every((value): value is bigint => value !== null && value !== 0n);
    const total = canonicalScaleFour(totalAmount);
    const residual = hasValidAmounts && total !== '' ? decimalSubtract(total, decimalSum(resolvedDraft.map((part) => part.amount))) : null;
    const signsMatch = hasValidAmounts && parsedAmounts.every((value) => (value < 0n) === ((parsedAmounts[0] ?? 0n) < 0n));
    const percentages = resolvedDraft.map((part) => parseScaled(part.percent));
    const percentagesValid = mode !== 'percent' || (percentages.every((value): value is bigint => value !== null && value >= 0n && value <= 100n * SCALE) && decimalIsZero(decimalSubtract(formatScaled(percentages.reduce((sum, value) => sum + value, 0n)), '100.0000')));
    const categoryIds = resolvedDraft.map((part) => part.categoryId);
    const categoriesValid = mode !== 'categories' || (categoryIds.every((id) => id !== '' && categoryById.has(id)) && new Set(categoryIds).size === categoryIds.length);
    const months = resolvedDraft.map((part) => monthIndex(part.month));
    const monthsValid = mode !== 'months' || (months.every((value): value is number => value !== null) && months.every((value, index) => index === 0 || value === (months[index - 1] ?? 0) + 1));
    const exact = residual !== null && decimalIsZero(residual);
    let message = '';
    if (resolvedDraft.length < 2) message = 'A split needs at least 2 parts.';
    else if (!hasValidAmounts || !signsMatch) message = 'Enter each part as a signed amount with 4 decimal places.';
    else if (!categoriesValid && mode === 'categories' && categoryIds.some((id) => id === '')) message = 'Choose a category for each part.';
    else if (!categoriesValid && mode === 'categories') message = 'Each category can appear only once.';
    else if (!monthsValid && mode === 'months') message = 'Months must be consecutive and increasing.';
    else if (!percentagesValid && mode === 'percent') message = 'Percents must total 100.0000%.';
    else if (!exact) message = 'Parts total must equal the transaction.';
    return { hasValidAmounts, residual, exact, categoriesValid, monthsValid, percentagesValid, message, valid: exact && categoriesValid && monthsValid && percentagesValid && resolvedDraft.length >= 2 && signsMatch };
  }, [categoryById, mode, resolvedDraft, totalAmount]);

  const splitBarParts = resolvedDraft
    .filter((part) => parseScaled(part.amount) !== null)
    .map((part, index) => ({ amount: part.amount, label: categoryById.get(part.categoryId)?.name ?? `Part ${index + 1}` }));

  function updatePart(index: number, patch: Partial<DraftPart>) {
    setDraft((current) => current.map((part, partIndex) => (partIndex === index ? { ...part, ...patch } : part)));
  }

  function selectMode(nextMode: SplitMode) {
    setMode(nextMode);
    if (nextMode === 'percent') {
      setDraft((current) => current.map((part) => ({ ...part, percent: part.percent || percentForAmount(part.amount, totalAmount) })));
    } else if (nextMode === 'months') {
      setDraft((current) => {
        const start = current.find((part) => part.month)?.month ?? initialMonth;
        const months = start ? consecutiveMonths(start, current.length) : current.map((part) => part.month);
        return current.map((part, index) => ({ ...part, month: months[index] ?? '' }));
      });
    }
  }

  function setShortcut(shortcut: 'equal' | 3 | 4 | 5 | 'custom') {
    const count = shortcut === 'equal' ? 2 : shortcut === 'custom' ? Math.max(2, Math.min(50, draft.length || 2)) : shortcut;
    if (shortcut === 'custom') {
      setDraft((current) => current.length >= 2 ? current : partsFromLineItems(sourceItems, categories));
      return;
    }
    const amounts = equalAmounts(totalAmount, count);
    const percents = equalPercents(count);
    const months = mode === 'months' ? consecutiveMonths(draft.find((part) => part.month)?.month ?? initialMonth ?? '', count) : [];
    setDraft((current) => Array.from({ length: count }, (_, index) => ({
      clientPartId: current[index]?.clientPartId ?? newUuid(), amount: amounts[index] ?? '', percent: percents[index] ?? '', categoryId: current[index]?.categoryId ?? '', taxonomyVersion: current[index]?.taxonomyVersion ?? '', businessState: current[index]?.businessState ?? 'personal', month: months[index] ?? '',
    })));
  }

  function addPart() {
    setDraft((current) => [...current, { clientPartId: newUuid(), amount: '0.0000', percent: '0.0000', categoryId: '', taxonomyVersion: '', businessState: 'personal', month: '' }]);
  }

  function removePart(index: number) {
    setDraft((current) => current.filter((_, partIndex) => partIndex !== index));
  }

  function requestParts(): SplitTransactionRequest['parts'] {
    return resolvedDraft.map((part) => ({
      client_part_id: part.clientPartId,
      amount: canonicalScaleFour(part.amount),
      business_state: part.businessState,
      ...(mode === 'categories' ? { category: { category_id: part.categoryId, taxonomy_version: part.taxonomyVersion } } : {}),
      ...(mode === 'percent' ? { allocation_percent: canonicalScaleFour(part.percent) } : {}),
      ...(mode === 'months' ? { allocation_month: part.month } : {}),
    }));
  }

  async function save() {
    if (!transactionId || revision < 1 || !validity.valid || splitWriter.isPending) return;
    setAction({ kind: 'saving' });
    try {
      const result = await splitWriter.mutateAsync({ transactionId, input: { expected_revision: revision, expected_line_items: expected, mode, parts: requestParts() } });
      if (!isSuccess(result)) {
        if (result.kind === 'conflict' && result.code === 'changed_since') setAction({ kind: 'save-error', ...messageForError({ code: result.code }, 'save') });
        else if (result.kind === 'unavailable') setAction({ kind: 'save-error', message: "We couldn't confirm the split. Refresh to check the current split.", refresh: true });
        else setAction({ kind: 'save-error', message: "Couldn't save this split. Try again.", refresh: false });
        return;
      }
      const nextItems = result.data.line_items;
      setSourceItems(nextItems);
      setExpectedItems(nextItems.map((item) => ({ line_item_id: item.line_item_id, version: item.version ?? 1 })));
      setDraft(partsFromLineItems(nextItems, categories));
      setEditing(false);
      setAction({ kind: 'saved', batchId: result.data.batch_id, mode: result.data.mode, count: nextItems.length, replay: result.kind === 'replay' });
      onSaved?.(result.data);
    } catch (error) {
      setAction({ kind: 'save-error', ...messageForError(error, 'save') });
    }
  }

  async function undo() {
    if (action.kind !== 'saved' || undoWriter.isPending) return;
    setAction({ kind: 'undoing' });
    try {
      const result = await undoWriter.mutateAsync({ batchId: action.batchId });
      if (!isSuccess(result)) {
        if (result.kind === 'conflict' && result.code === 'changed_since') setAction({ kind: 'undo-error', ...messageForError({ code: result.code }, 'undo') });
        else if (result.kind === 'unavailable') setAction({ kind: 'undo-error', message: "We couldn't confirm the split Undo. Refresh to check the current split.", refresh: true });
        else setAction({ kind: 'undo-error', message: "Couldn't undo this split. Try again.", refresh: false });
        return;
      }
      if (result.data.line_items) {
        setSourceItems(result.data.line_items);
        setExpectedItems(result.data.line_items.map((item) => ({ line_item_id: item.line_item_id, version: item.version ?? 1 })));
        setDraft(partsFromLineItems(result.data.line_items, categories));
      }
      setAction({ kind: 'idle' });
      await queryClient.invalidateQueries({ queryKey: ['transactions', 'detail', transactionId] });
      await queryClient.refetchQueries({ queryKey: ['transactions', 'detail', transactionId], type: 'active' });
      onUndone?.(result.data);
      setEditing(false);
      window.setTimeout(() => openButtonRef.current?.focus(), 0);
    } catch (error) {
      setAction({ kind: 'undo-error', ...messageForError(error, 'undo') });
    }
  }

  function refresh() {
    onRefresh?.();
    if (!onRefresh) void queryClient.invalidateQueries({ queryKey: ['transactions', 'detail', transactionId] });
  }

  function cancel() {
    setDraft(partsFromLineItems(sourceItems, categories));
    setMode('amount');
    setAction({ kind: 'idle' });
    setEditing(false);
    onCancel?.();
  }

  if (!editing) {
    return (
      <div data-testid="split-saved" className="mt-3 rounded-lg border border-border bg-card p-3.5">
        {action.kind === 'saved' ? (
          <div className="flex flex-wrap items-center gap-2 text-sm" role="status">
            <span>Saved · split {action.mode === 'amount' ? 'by amount' : action.mode === 'percent' ? 'by percent' : action.mode === 'categories' ? 'across categories' : 'over months'} into {action.count} {action.count === 1 ? 'part' : 'parts'}</span>
            <button type="button" className="min-h-11 rounded-md border border-input px-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onClick={() => void undo()} disabled={undoWriter.isPending}>Undo split</button>
          </div>
        ) : action.kind === 'undo-error' ? (
          <div role="alert" className="flex flex-wrap items-center gap-2 text-sm text-degraded-foreground"><span>{action.message}</span>{action.refresh && <button type="button" className="min-h-11 rounded-md border border-input px-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onClick={refresh}>Refresh</button>}</div>
        ) : null}
        <button ref={openButtonRef} type="button" className="mt-2 min-h-11 rounded-md border border-input px-3 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onClick={() => { setAction({ kind: 'idle' }); setEditing(true); }}>Split this transaction</button>
      </div>
    );
  }

  const errorAction = action.kind === 'save-error' || action.kind === 'undo-error' ? action : null;
  const isSaving = action.kind === 'saving' || Boolean(splitWriter.isPending);

  return (
    <div data-testid="split-editor" className="mt-3 rounded-lg border border-border bg-card p-3.5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">Split · {resolvedDraft.length} parts</h3>
          <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"><div><dt className="inline font-medium">Bank amount </dt><dd className="inline"><AmountText value={totalAmount} currency={currency} /></dd></div><div><dt className="inline font-medium">Transaction currency </dt><dd className="inline">{currency}</dd></div></dl>
        </div>
        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Split mode">
          {([
            ['amount', 'By amount'], ['percent', 'By percent'], ['categories', 'Across categories'], ['months', 'Spread over months'],
          ] as const).map(([value, label]) => <button key={value} type="button" aria-pressed={mode === value} className="min-h-11 rounded-full border border-input px-3 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 aria-pressed:bg-muted" onClick={() => selectMode(value)}>{label}</button>)}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground"><span>Shortcuts:</span><button type="button" className="min-h-11 rounded-md border border-input px-3 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onClick={() => setShortcut('equal')}>Equal</button>{[3, 4, 5].map((count) => <button key={count} type="button" className="min-h-11 rounded-md border border-input px-3 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onClick={() => setShortcut(count as 3 | 4 | 5)}>{count}-way</button>)}<button type="button" className="min-h-11 rounded-md border border-input px-3 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onClick={() => setShortcut('custom')}>Custom</button></div>

      <p className="mt-2 text-xs text-muted-foreground">{mode === 'amount' && 'Enter each part as an amount. Parts must add up to exactly the bank amount.'}{mode === 'percent' && 'Enter each part as a percent of the charge. Percents must total 100.0000%; amounts are shown as they will be saved.'}{mode === 'categories' && 'One part per category; each part gets an amount. The same category cannot appear twice.'}{mode === 'months' && 'Spread the charge evenly across consecutive months. Each month becomes its own part; the bank amount stays as one charge.'}</p>

      <div className="mt-2.5 grid gap-2">{resolvedDraft.map((part, index) => <div key={part.clientPartId} className="grid grid-cols-[minmax(0,1fr)_minmax(132px,auto)_min-h-11] items-center gap-2">
        {mode === 'months' ? <label className="flex min-h-11 items-center text-sm" htmlFor={`split-month-${part.clientPartId}`}>Part {index + 1} month</label> : mode === 'categories' ? <label className="flex min-h-11 items-center text-sm" htmlFor={`split-category-${part.clientPartId}`}>Part {index + 1} category</label> : <span className="flex min-h-11 items-center text-sm">Part {index + 1}</span>}
        {mode === 'categories' && <select id={`split-category-${part.clientPartId}`} aria-label={`Part ${index + 1} category`} value={part.categoryId} className="min-h-11 min-w-40 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onChange={(event) => { const category = categoryById.get(event.target.value); updatePart(index, { categoryId: event.target.value, taxonomyVersion: category?.taxonomy_version ?? '' }); }}><option value="">Choose a category</option>{categories.map((category) => <option key={category.category_id} value={category.category_id}>{category.name}</option>)}</select>}
        {mode === 'months' && <input id={`split-month-${part.clientPartId}`} type="month" aria-label={`Part ${index + 1} month`} value={part.month} className="min-h-11 min-w-36 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onChange={(event) => updatePart(index, { month: event.target.value })} />}
        {mode !== 'categories' && mode !== 'months' && <span aria-hidden="true" />}
        {mode === 'percent' ? <div className="flex min-h-11 items-center gap-1"><input type="text" inputMode="decimal" aria-label={`Part ${index + 1} percent`} value={part.percent} className="min-h-11 w-28 rounded-md border border-input bg-background px-2 text-right text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onChange={(event) => updatePart(index, { percent: event.target.value })} /><span className="text-xs text-muted-foreground">%</span><input type="text" aria-label={`Part ${index + 1} amount`} value={part.amount} readOnly className="min-h-11 w-36 rounded-md border border-input bg-muted px-2 text-right text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" /></div> : <input type="text" inputMode="decimal" aria-label={`Part ${index + 1} amount`} value={part.amount} readOnly={mode === 'months'} className="min-h-11 w-36 rounded-md border border-input bg-background px-2 text-right text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 read-only:bg-muted" onChange={(event: ChangeEvent<HTMLInputElement>) => updatePart(index, { amount: event.target.value })} />}
        <button type="button" aria-label={`Remove part ${index + 1}`} className="min-h-11 min-w-11 rounded-md text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onClick={() => removePart(index)}>×</button>
      </div>)}</div>

      <button type="button" className="mt-1 min-h-11 rounded-md px-2 text-sm text-link underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onClick={addPart}>+ Add part</button>
      <SplitBar className="mt-2.5" total={totalAmount} unallocated={validity.residual ?? totalAmount} parts={splitBarParts} />

      {validity.residual !== null && !decimalIsZero(validity.residual) && <div data-testid="split-residual" className="mt-3 flex items-center justify-between border-t border-border pt-2.5 text-xs text-degraded-foreground"><span>Unallocated · declared parts do not equal the charge</span><AmountText value={validity.residual} currency={currency} /></div>}

      <div data-testid="split-total" role="status" className={`mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-2.5 text-sm ${validity.valid ? '' : 'text-degraded-foreground'}`}><span>{validity.valid ? 'Parts total · equals the transaction' : validity.message}{!validity.valid && validity.residual !== null && <>{' (off by '}<AmountText value={validity.residual} currency={currency} />{')'}</>}</span><span className="text-right"><AmountText value={validity.hasValidAmounts ? decimalSum(resolvedDraft.map((part) => part.amount)) : null} currency={currency} /> / <AmountText value={totalAmount} currency={currency} /></span></div>

      {errorAction && <div role="alert" className="mt-2 flex flex-wrap items-center gap-2 text-sm text-degraded-foreground"><span>{errorAction.message}</span>{errorAction.refresh && <button type="button" className="min-h-11 rounded-md border border-input px-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onClick={refresh}>Refresh</button>}</div>}
      <div className="mt-2.5 flex flex-wrap gap-2"><button type="button" data-testid="split-save-button" className="min-h-11 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50" disabled={!validity.valid || isSaving} onClick={() => void save()}>{isSaving ? 'Saving…' : 'Save split'}</button><button type="button" className="min-h-11 rounded-md border border-input px-4 text-sm font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2" onClick={cancel} disabled={isSaving}>Cancel</button></div>
    </div>
  );
}

export default SplitEditor;
